// Wiederverwendbarer Planungs-Copilot (KI-Hinweis-Panel).
//
// Rein darstellend: bekommt eine deterministische RoofCopilotAdvice (aus
// roof-core buildRoofCopilotAdvice) und zeigt Headline, nächsten Schritt,
// priorisierte Hinweise + Pflicht-Disclaimer. Gibt NICHTS frei (canApprove=false).
// Kontextsensitiv durch die übergebene Advice; sendet selbst nichts nach außen.
import type { RoofCopilotAdvice, CopilotSeverity } from "@energyos/roof-core";

const SEV_COLOR: Record<CopilotSeverity, string> = {
  critical: "#dc2626",
  warning: "#d97706",
  info: "#2563eb",
};

const READINESS_BADGE: Record<RoofCopilotAdvice["readiness"], { label: string; bg: string; fg: string }> = {
  ready: { label: "bereit", bg: "#f0fdf4", fg: "#166534" },
  caution: { label: "Hinweise offen", bg: "#fffbeb", fg: "#92400e" },
  not_ready: { label: "noch nicht bereit", bg: "#fef2f2", fg: "#991b1b" },
};

export function PlanningCopilot({
  advice,
  title = "KI-Copilot",
  compact = false,
}: {
  advice: RoofCopilotAdvice;
  title?: string;
  compact?: boolean;
}) {
  const badge = READINESS_BADGE[advice.readiness];
  return (
    <div
      data-testid="planning-copilot"
      style={{
        border: "1px solid #c7d2fe",
        borderRadius: 8,
        background: "#f5f7ff",
        overflow: "hidden",
        fontSize: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#eef2ff", borderBottom: "1px solid #c7d2fe" }}>
        <span style={{ fontWeight: 700, color: "#3730a3", display: "flex", alignItems: "center", gap: 6 }}>
          <span aria-hidden>🤖</span> {title}
        </span>
        <span style={{ fontSize: 10, fontWeight: 700, color: badge.fg, background: badge.bg, border: `1px solid ${badge.fg}33`, borderRadius: 4, padding: "1px 6px" }}>
          {badge.label}
        </span>
      </div>

      <div style={{ padding: "8px 12px" }}>
        <div style={{ fontWeight: 600, color: "#1e293b", marginBottom: 6 }}>{advice.headline}</div>

        {advice.nextStep && (
          <div style={{ marginBottom: 8, padding: "6px 8px", background: "#fff", border: "1px solid #c7d2fe", borderRadius: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#4338ca" }}>NÄCHSTER SCHRITT</span>
            <div style={{ color: "#1e293b" }}>{advice.nextStep}</div>
          </div>
        )}

        {!compact &&
          advice.hints.map((h) => (
            <div key={h.code} style={{ display: "flex", gap: 7, padding: "3px 0", alignItems: "flex-start" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: SEV_COLOR[h.severity], flexShrink: 0, marginTop: 4 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: h.severity === "critical" ? "#991b1b" : "#334155" }}>{h.title}</div>
                <div style={{ color: "#64748b", fontSize: 11 }}>{h.detail}</div>
                <div style={{ color: "#4338ca", fontSize: 11 }}>→ {h.nextStep}</div>
              </div>
            </div>
          ))}

        <div style={{ marginTop: 8, fontSize: 10, color: "#94a3b8", fontStyle: "italic", borderTop: "1px dashed #c7d2fe", paddingTop: 6 }}>
          {advice.disclaimer}
        </div>
      </div>
    </div>
  );
}
